<template>
    <section class="mobile-network-page">
        <van-nav-bar title="网络版管理" left-arrow fixed placeholder z-index="100" safe-area-inset-top
                     @click-left="goBack"/>
        <main class="mobile-network-main">
            <section class="mobile-network-hero">
                <div class="mobile-network-hero__icon">
                    <van-icon name="diamond-o"/>
                </div>
                <div>
                    <span>ENTERPRISE LICENSE</span>
                    <h1>企业网络版</h1>
                    <p>一个节点对应一名授权用户，加入后即可获得企业提供的产品权益。</p>
                </div>
            </section>

            <div v-if="loading" class="mobile-network-state">
                <van-loading color="#6f57cf">正在加载网络版</van-loading>
            </div>
            <van-empty v-else-if="!companies.length" image="network" description="暂无可管理的网络版"/>

            <template v-else>
                <section class="mobile-network-company-section">
                    <div class="mobile-network-section-title">
                        <div><h2>企业授权</h2>
                            <p>选择需要管理的企业</p></div>
                        <span>{{ companies.length }} 家</span>
                    </div>

                    <div class="mobile-network-company-list">
                        <button
                            v-for="company in companies"
                            :key="company.net_id"
                            :class="['mobile-network-company', {active: company.net_id === currentNetId}]"
                            type="button"
                            @click="selectCompany(company)"
                        >
                            <div class="mobile-network-company__head">
                                <span><van-icon name="gem-o"/> 网络版</span>
                                <van-icon v-if="company.net_id === currentNetId" name="success"/>
                            </div>
                            <strong>{{ company.company_name_alias || '未命名企业' }}</strong>
                            <small>{{ company.manager_name || '—' }} ·
                                {{ formatTime(company.end_time_str || company.end_time, true) }}</small>
                        </button>
                    </div>
                </section>

                <section class="mobile-network-license-card">
                    <header>
                        <div>
                            <span>NODE LICENSE</span>
                            <h2>{{ currentCompany.company_name_alias || '未命名企业' }}</h2>
                        </div>
                        <div class="mobile-network-license-card__vip">
                            <van-icon name="vip-card"/>
                            企业 VIP
                        </div>
                    </header>

                    <div class="mobile-network-node-count">
                        <div><strong>{{ usedNodes }}</strong><span>已使用节点</span></div>
                        <i></i>
                        <div><strong>{{ totalNodes }}</strong><span>节点总数</span></div>
                        <i></i>
                        <div><strong>{{ availableNodes }}</strong><span>剩余节点</span></div>
                    </div>

                    <div class="mobile-network-progress">
                        <div><span>节点使用率</span><strong>{{ nodePercent }}%</strong></div>
                        <van-progress :percentage="nodePercent" :show-pivot="false" stroke-width="7"
                                      color="linear-gradient(to right, #b58a31, #f0cc75)"/>
                    </div>

                    <div class="mobile-network-license-meta">
                        <div>
                            <van-icon name="manager-o"/>
                            <span>管理员</span><strong>{{ currentCompany.manager_name || '—' }}</strong></div>
                        <div>
                            <van-icon name="underway-o"/>
                            <span>权益截止</span><strong>{{
                                formatTime(currentCompany.end_time_str || currentCompany.end_time)
                            }}</strong></div>
                    </div>

                    <div class="mobile-network-license-actions">
                        <van-button round plain icon="edit" @click="openEditCompany">修改网络版</van-button>
                        <van-button round plain icon="manager-o" @click="openTransferManager">转让管理员</van-button>
                    </div>
                </section>

                <section class="mobile-network-users">
                    <div class="mobile-network-section-title mobile-network-users__heading">
                        <div><h2>授权用户</h2>
                            <p>每添加一名用户占用一个节点</p></div>
                        <van-button round type="info" size="small" icon="plus" :disabled="availableNodes <= 0"
                                    @click="openAddUser">
                            添加用户
                        </van-button>
                    </div>

                    <van-search v-model="keyword" shape="round" placeholder="搜索客户号、姓名或手机号"/>

                    <div v-if="filteredUsers.length" class="mobile-network-user-list">
                        <article v-for="user in filteredUsers" :key="user.mapping_id || user.username"
                                 class="mobile-network-user-card">
                            <div class="mobile-network-user-card__avatar">{{ userInitial(user) }}</div>
                            <div class="mobile-network-user-card__body">
                                <div><strong>{{ user.nickname || '未设置备注' }}</strong>
                                    <van-tag round :type="Number(user.bind_status) === 1 ? 'success' : 'default'">
                                        {{ Number(user.bind_status) === 1 ? '已启用' : '已禁用' }}
                                    </van-tag>
                                </div>
                                <p>{{ user.username || user.phone || '—' }}</p>
                                <small>添加时间 {{ formatTime(user.create_time) }}</small>
                            </div>
                            <div class="mobile-network-user-card__actions">
                                <van-switch
                                    :value="Number(user.bind_status) === 1"
                                    size="22px"
                                    active-color="#6f57cf"
                                    @input="changeUserStatus(user, $event)"
                                />
                                <button type="button" @click="openChangeUser(user)">
                                    <van-icon name="edit"/>
                                    修改用户
                                </button>
                            </div>
                        </article>
                    </div>
                    <van-empty v-else image="search" description="没有找到授权用户"/>
                </section>
            </template>
        </main>

        <van-popup v-model="addUserVisible" position="bottom" round safe-area-inset-bottom closeable>
            <section class="mobile-network-form">
                <header><span>ADD MEMBER</span>
                    <h2>添加授权用户</h2>
                    <p>添加成功后将占用 1 个企业节点。</p></header>
                <van-form @submit="submitAddUser">
                    <van-field
                        v-model.trim="userForm.phone"
                        name="phone"
                        label="客户号"
                        placeholder="支持电话号码或客户号"
                        clearable
                        :rules="[{required: true, message: '请输入客户号'}]"
                    />
                    <van-field v-model.trim="userForm.nickname" name="nickname" label="姓名备注"
                               placeholder="请输入用户姓名或备注" clearable/>
                    <div class="mobile-network-form__submit">
                        <van-button block round type="info" native-type="submit" :loading="submitting">确认添加
                        </van-button>
                    </div>
                </van-form>
            </section>
        </van-popup>

        <van-popup v-model="editCompanyVisible" position="bottom" round safe-area-inset-bottom closeable>
            <section class="mobile-network-form">
                <header><span>EDIT LICENSE</span>
                    <h2>修改网络版</h2>
                    <p>修改企业网络版的对外显示名称。</p></header>
                <van-form @submit="submitEditCompany">
                    <van-field
                        v-model.trim="companyForm.company_name_alias"
                        name="company_name_alias"
                        label="企业名称"
                        placeholder="请输入企业显示名称"
                        clearable
                        :rules="[{required: true, message: '请输入企业名称'}]"
                    />
                    <div class="mobile-network-form__submit">
                        <van-button block round type="info" native-type="submit" :loading="submitting">保存修改
                        </van-button>
                    </div>
                </van-form>
            </section>
        </van-popup>

        <van-popup v-model="changeUserVisible" position="bottom" round safe-area-inset-bottom closeable>
            <section class="mobile-network-form">
                <header>
                    <span>CHANGE MEMBER</span>
                    <h2>修改用户</h2>
                    <p>当前用户：{{ changeUserForm.old_nickname || '—' }}，本年度还可修改 {{ remainingChangeTimes }}
                        次。</p>
                </header>
                <van-form @submit="submitChangeUser">
                    <van-field
                        v-model.trim="changeUserForm.phone"
                        name="phone"
                        label="新客户号"
                        placeholder="支持电话号码或客户号"
                        clearable
                        :rules="[{required: true, message: '请输入新客户号'}]"
                    />
                    <van-field v-model.trim="changeUserForm.nickname" name="nickname" label="姓名备注"
                               placeholder="请输入新用户姓名或备注" clearable/>
                    <div class="mobile-network-form__notice">
                        <van-icon name="warning-o"/>
                        每个用户一年内最多可修改 3 次，次年可重新获得 3 次。
                    </div>
                    <div class="mobile-network-form__submit">
                        <van-button block round type="info" native-type="submit" :loading="submitting">确认修改
                        </van-button>
                    </div>
                </van-form>
            </section>
        </van-popup>

        <van-popup v-model="transferManagerVisible" position="bottom" round safe-area-inset-bottom closeable>
            <section class="mobile-network-form mobile-network-manager-form">
                <header><span>TRANSFER MANAGER</span>
                    <h2>转让管理员</h2>
                    <p>选择新管理员。转让后，您将不能再管理该网络版。</p></header>
                <van-radio-group v-model="managerUsername">
                    <van-cell-group>
                        <van-cell v-for="user in currentUsers" :key="user.mapping_id || user.username" clickable
                                  @click="managerUsername = user.username">
                            <template #title>
                                <div class="mobile-network-manager-option"><strong>{{
                                        user.nickname || user.username
                                    }}</strong><span>{{ user.username }}</span></div>
                            </template>
                            <template #right-icon>
                                <van-radio :name="user.username" checked-color="#6f57cf"/>
                            </template>
                        </van-cell>
                    </van-cell-group>
                </van-radio-group>
                <div class="mobile-network-form__submit">
                    <van-button block round type="info" :disabled="!managerUsername" :loading="submitting"
                                @click="submitTransferManager">确认转让
                    </van-button>
                </div>
            </section>
        </van-popup>
    </section>
</template>

<script>
export default {
    name: 'MobileNetworkManage',
    data() {
        return {
            baseUrl: '/bbs/net/list',
            baseAddUrl: '/bbs/net/jo',
            baseManageUrl: '/bbs/net/rma',
            baseChangeUrl: '/bbs/net/cun',
            loading: true,
            submitting: false,
            companies: [],
            currentNetId: '',
            keyword: '',
            addUserVisible: false,
            editCompanyVisible: false,
            changeUserVisible: false,
            transferManagerVisible: false,
            managerUsername: '',
            userForm: {phone: '', nickname: ''},
            companyForm: {company_name_alias: ''},
            changeUserForm: {id: '', old_username: '', old_nickname: '', phone: '', nickname: '', times: 0},
        };
    },
    computed: {
        currentCompany() {
            return this.companies.find(item => item.net_id === this.currentNetId) || {};
        },
        currentUsers() {
            return Array.isArray(this.currentCompany.users) ? this.currentCompany.users : [];
        },
        filteredUsers() {
            const keyword = this.keyword.trim().toLowerCase();
            if (!keyword) return this.currentUsers;
            return this.currentUsers.filter(user => [user.nickname, user.username, user.phone].some(value => String(value || '').toLowerCase().includes(keyword)));
        },
        totalNodes() {
            return Number(this.currentCompany.net_sum) || 0;
        },
        usedNodes() {
            return this.currentUsers.length;
        },
        availableNodes() {
            return Math.max(this.totalNodes - this.usedNodes, 0);
        },
        nodePercent() {
            if (!this.totalNodes) return 0;
            return Math.min(Math.round(this.usedNodes / this.totalNodes * 100), 100);
        },
        remainingChangeTimes() {
            return Math.max(3 - (Number(this.changeUserForm.times) || 0), 0);
        },
    },
    created() {
        this.loadCompanies();
    },
    methods: {
        goBack() {
            this.$router.push({path: '/user/profile/setting'});
        },
        loadCompanies(preferredNetId) {
            this.loading = true;
            this.$bbs.get(this.baseUrl, {
                params: {search: {}, sort: {}, total: 1, pageSize: 15, pageNumber: 1},
                need: {error: true},
            }).then(response => {
                this.companies = Array.isArray(response.data && response.data.detail) ? response.data.detail : [];
                const selected = this.companies.find(item => item.net_id === (preferredNetId || this.currentNetId));
                this.currentNetId = (selected || this.companies[0] || {}).net_id || '';
            }).finally(() => {
                this.loading = false;
            });
        },
        selectCompany(company) {
            this.currentNetId = company.net_id;
            this.keyword = '';
        },
        openAddUser() {
            if (this.availableNodes <= 0) {
                this.$toast('当前企业节点已用完');
                return;
            }
            this.userForm = {phone: '', nickname: ''};
            this.addUserVisible = true;
        },
        submitAddUser() {
            this.submitting = true;
            this.$bbs.post(this.baseAddUrl, {
                net_id: this.currentNetId,
                phone: this.userForm.phone,
                nickname: this.userForm.nickname,
            }, {need: {error: true}}).then(() => {
                this.$toast.success('用户添加成功');
                this.addUserVisible = false;
                this.loadCompanies(this.currentNetId);
            }).finally(() => {
                this.submitting = false;
            });
        },
        openEditCompany() {
            this.companyForm = {company_name_alias: this.currentCompany.company_name_alias || ''};
            this.editCompanyVisible = true;
        },
        submitEditCompany() {
            this.submitting = true;
            this.$bbs.put(this.baseUrl, {
                net_id: this.currentNetId,
                company_name_alias: this.companyForm.company_name_alias,
            }, {need: {error: true}}).then(response => {
                const name = response.data && response.data.company_name_alias || this.companyForm.company_name_alias;
                this.$set(this.currentCompany, 'company_name_alias', name);
                this.editCompanyVisible = false;
                this.$toast.success('网络版信息已更新');
            }).finally(() => {
                this.submitting = false;
            });
        },
        openChangeUser(user) {
            if (!user._can_change) {
                const nextTime = user.change_time ? `\n可再次修改时间：${this.formatTime(user.change_time)}` : '';
                this.$dialog.alert({
                    title: '暂时无法修改',
                    message: `该用户本年度的修改次数已用完。${nextTime}`,
                    confirmButtonText: '我知道了',
                    confirmButtonColor: '#6f57cf',
                });
                return;
            }
            this.changeUserForm = {
                id: user.mapping_id,
                old_username: user.username,
                old_nickname: user.nickname || user.username,
                phone: '',
                nickname: '',
                times: Number(user.times) || 0,
            };
            this.changeUserVisible = true;
        },
        submitChangeUser() {
            this.submitting = true;
            this.$bbs.post(this.baseChangeUrl, {
                net_id: this.currentNetId,
                id: this.changeUserForm.id,
                old_username: this.changeUserForm.old_username,
                phone: this.changeUserForm.phone,
                nickname: this.changeUserForm.nickname,
            }, {need: {error: true}}).then(() => {
                this.changeUserVisible = false;
                this.$toast.success('用户修改成功');
                this.loadCompanies(this.currentNetId);
            }).finally(() => {
                this.submitting = false;
            });
        },
        openTransferManager() {
            if (!this.currentUsers.length) {
                this.$toast('当前没有可转让的授权用户');
                return;
            }
            this.managerUsername = '';
            this.transferManagerVisible = true;
        },
        submitTransferManager() {
            const user = this.currentUsers.find(item => item.username === this.managerUsername) || {};
            this.$dialog.confirm({
                title: '最后确认',
                message: `确认将管理员转让给“${user.nickname || user.username}”吗？转让后您将不能继续管理该网络版。`,
                confirmButtonText: '确认转让',
                cancelButtonText: '取消',
                confirmButtonColor: '#d14f56',
                closeOnClickOverlay: true,
            }).then(() => {
                this.submitting = true;
                return this.$bbs.put(this.baseManageUrl, {
                    net_id: this.currentNetId,
                    username: this.managerUsername,
                }, {need: {error: true}});
            }).then(() => {
                this.transferManagerVisible = false;
                this.$toast.success('管理员已转让');
                this.loadCompanies();
            }).catch(() => {
            }).finally(() => {
                this.submitting = false;
            });
        },
        changeUserStatus(user, enabled) {
            const nextStatus = enabled ? 1 : 0;
            this.$dialog.confirm({
                title: enabled ? '启用用户' : '禁用用户',
                message: `确认${enabled ? '启用' : '禁用'}“${user.nickname || user.username}”的企业权益吗？`,
                confirmButtonText: '确认',
                cancelButtonText: '取消',
                confirmButtonColor: '#6f57cf',
                closeOnClickOverlay: true,
            }).then(() => this.$bbs.put(this.baseAddUrl, {
                from_type: 'buy',
                mapping_id: user.mapping_id,
                nickname: user.nickname,
                bind_status: nextStatus,
                net_id: this.currentNetId,
            }, {need: {error: true}})).then(() => {
                this.$set(user, 'bind_status', nextStatus);
                this.$toast.success('状态已更新');
            }).catch(() => {
            });
        },
        userInitial(user) {
            const text = user.nickname || user.username || 'U';
            return String(text).slice(0, 1).toUpperCase();
        },
        formatTime(value, dateOnly = false) {
            if (!value) return '—';
            if (typeof value === 'string' && !/^\d{10,13}$/.test(value)) {
                const stringDate = new Date(value.replace(/-/g, '/'));
                if (!isNaN(stringDate.getTime()) && stringDate.getFullYear() >= 2100) return '终身会员';
                return dateOnly ? value.slice(0, 10) : value;
            }
            const number = Number(value);
            const date = new Date(number < 1000000000000 ? number * 1000 : number);
            if (!isNaN(date.getTime()) && date.getFullYear() >= 2100) return '终身会员';
            const pad = item => String(item).padStart(2, '0');
            const result = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
            return dateOnly ? result : `${result} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
        },
    },
};
</script>

<style scoped lang="less">
.mobile-network-page {
    min-height: 100vh;
    color: #1d2534;
    background: #f4f5f8;
}

.mobile-network-page /deep/ .van-nav-bar--fixed {
    z-index: 100 !important;
    background: #fff;
    box-shadow: 0 1px 8px rgba(24, 34, 53, .06);
}

.mobile-network-page /deep/ .van-nav-bar__title {
    font-size: 17px;
    font-weight: 650;
}

.mobile-network-main {
    padding: 12px 12px 32px;
}

.mobile-network-hero {
    position: relative;
    display: flex;
    overflow: hidden;
    min-height: 145px;
    padding: 23px 18px;
    box-sizing: border-box;
    align-items: center;
    color: #fff;
    background: radial-gradient(circle at 90% 8%, rgba(230, 196, 116, .42), transparent 32%), linear-gradient(140deg, #171c33, #403177 72%, #634ba8);
    border-radius: 19px;
    box-shadow: 0 12px 30px rgba(57, 41, 119, .22);
    gap: 14px;
}

.mobile-network-hero__icon {
    display: flex;
    width: 48px;
    height: 48px;
    flex: 0 0 48px;
    align-items: center;
    justify-content: center;
    color: #f3cf7b;
    font-size: 27px;
    background: rgba(255, 255, 255, .1);
    border: 1px solid rgba(255, 230, 166, .25);
    border-radius: 15px;
}

.mobile-network-hero span, .mobile-network-license-card header span {
    color: #d6ba78;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 1.3px;
}

.mobile-network-hero h1 {
    margin: 5px 0 7px;
    font-size: 23px;
}

.mobile-network-hero p {
    margin: 0;
    color: rgba(255, 255, 255, .72);
    font-size: 13px;
    line-height: 1.55;
}

.mobile-network-state {
    display: flex;
    min-height: 300px;
    align-items: center;
    justify-content: center;
}

.mobile-network-company-section, .mobile-network-users {
    margin-top: 21px;
}

.mobile-network-section-title {
    display: flex;
    padding: 0 4px 11px;
    align-items: flex-end;
    justify-content: space-between;
}

.mobile-network-section-title h2 {
    margin: 0 0 4px;
    font-size: 18px;
}

.mobile-network-section-title p {
    margin: 0;
    color: #929bab;
    font-size: 12px;
}

.mobile-network-section-title > span {
    padding: 4px 9px;
    color: #68588f;
    font-size: 11px;
    background: #e9e5f5;
    border-radius: 999px;
}

.mobile-network-company-list {
    display: flex;
    overflow-x: auto;
    margin: 0 -12px;
    padding: 0 12px 4px;
    scrollbar-width: none;
    gap: 10px;
}

.mobile-network-company-list::-webkit-scrollbar {
    display: none;
}

.mobile-network-company {
    width: 230px;
    padding: 14px;
    flex: 0 0 230px;
    color: #526078;
    text-align: left;
    background: #fff;
    border: 1px solid #e4e7ee;
    border-radius: 14px;
    box-shadow: 0 5px 16px rgba(32, 43, 65, .04);
}

.mobile-network-company.active {
    color: #fff;
    background: linear-gradient(135deg, #51408e, #755dc1);
    border-color: #6c54b6;
    box-shadow: 0 8px 20px rgba(87, 66, 170, .2);
}

.mobile-network-company__head {
    display: flex;
    margin-bottom: 9px;
    align-items: center;
    justify-content: space-between;
    font-size: 11px;
    opacity: .8;
}

.mobile-network-company__head span {
    display: flex;
    align-items: center;
    gap: 4px;
}

.mobile-network-company strong, .mobile-network-company small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.mobile-network-company strong {
    margin-bottom: 7px;
    font-size: 15px;
}

.mobile-network-company small {
    font-size: 11px;
    opacity: .7;
}

.mobile-network-license-card {
    position: relative;
    overflow: hidden;
    margin-top: 14px;
    padding: 19px 17px;
    background: linear-gradient(150deg, #fffdf8, #fff7e6);
    border: 1px solid rgba(207, 166, 75, .38);
    border-top: 3px solid #d6a743;
    border-radius: 17px;
    box-shadow: 0 9px 26px rgba(135, 91, 20, .11);
}

.mobile-network-license-card header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
}

.mobile-network-license-card h2 {
    margin: 5px 0 0;
    font-size: 19px;
}

.mobile-network-license-card__vip {
    display: flex;
    padding: 5px 8px;
    align-items: center;
    color: #795418;
    font-size: 10px;
    font-weight: 800;
    background: linear-gradient(135deg, #ffe9ad, #d8a540);
    border-radius: 999px;
    gap: 4px;
}

.mobile-network-node-count {
    display: flex;
    margin-top: 18px;
    padding: 14px 8px;
    align-items: center;
    justify-content: space-around;
    background: rgba(255, 255, 255, .68);
    border: 1px solid rgba(218, 181, 98, .22);
    border-radius: 12px;
}

.mobile-network-node-count div {
    text-align: center;
}

.mobile-network-node-count strong, .mobile-network-node-count span {
    display: block;
}

.mobile-network-node-count strong {
    color: #5f4218;
    font-size: 22px;
}

.mobile-network-node-count span {
    margin-top: 3px;
    color: #9a886b;
    font-size: 10px;
}

.mobile-network-node-count i {
    width: 1px;
    height: 29px;
    background: #eadfc6;
}

.mobile-network-progress {
    margin-top: 15px;
}

.mobile-network-progress > div {
    display: flex;
    margin-bottom: 7px;
    justify-content: space-between;
    color: #8a775a;
    font-size: 11px;
}

.mobile-network-license-meta {
    display: grid;
    margin-top: 16px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 9px;
}

.mobile-network-license-meta div {
    display: grid;
    padding: 10px;
    grid-template-columns: 18px 1fr;
    background: #fff;
    border: 1px solid #f0e8d8;
    border-radius: 10px;
}

.mobile-network-license-meta .van-icon {
    grid-row: span 2;
    color: #bd8b32;
    font-size: 15px;
}

.mobile-network-license-meta span {
    color: #a09078;
    font-size: 10px;
}

.mobile-network-license-meta strong {
    overflow: hidden;
    margin-top: 3px;
    color: #64543c;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.mobile-network-license-actions {
    display: grid;
    margin-top: 13px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 9px;
}

.mobile-network-license-actions /deep/ .van-button {
    height: 39px;
    color: #856328;
    font-size: 12px;
    background: rgba(255, 255, 255, .65);
    border-color: #d9bf88;
}

.mobile-network-users {
    overflow: hidden;
    background: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 17px;
    box-shadow: 0 7px 22px rgba(32, 45, 68, .05);
}

.mobile-network-users__heading {
    padding: 17px 15px 8px;
    align-items: center;
}

.mobile-network-users__heading /deep/ .van-button--info {
    background: linear-gradient(135deg, #8069dd, #5f48bd);
    border-color: #6d55c7;
}

.mobile-network-users /deep/ .van-search {
    padding: 7px 12px 11px;
}

.mobile-network-user-list {
    border-top: 1px solid #eef0f4;
}

.mobile-network-user-card {
    display: flex;
    padding: 14px 13px;
    align-items: center;
    border-bottom: 1px solid #eef0f4;
    gap: 11px;
}

.mobile-network-user-card:last-child {
    border-bottom: 0;
}

.mobile-network-user-card__avatar {
    display: flex;
    width: 39px;
    height: 39px;
    flex: 0 0 39px;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    background: linear-gradient(135deg, #8b74df, #5d47b5);
    border-radius: 12px;
}

.mobile-network-user-card__body {
    min-width: 0;
    flex: 1;
}

.mobile-network-user-card__body > div {
    display: flex;
    align-items: center;
    gap: 7px;
}

.mobile-network-user-card__body strong {
    overflow: hidden;
    font-size: 14px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.mobile-network-user-card__body p {
    margin: 4px 0 3px;
    color: #667286;
    font-size: 12px;
}

.mobile-network-user-card__body small {
    color: #a0a8b5;
    font-size: 10px;
}

.mobile-network-user-card__actions {
    display: flex;
    flex: 0 0 65px;
    align-items: flex-end;
    flex-direction: column;
    gap: 9px;
}

.mobile-network-user-card__actions button {
    padding: 0;
    color: #6e55c4;
    font-size: 11px;
    white-space: nowrap;
    background: transparent;
    border: 0;
}

.mobile-network-form {
    padding: 25px 14px 20px;
}

.mobile-network-form header {
    padding: 0 4px 18px;
}

.mobile-network-form header span {
    color: #8a72d2;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 1.2px;
}

.mobile-network-form header h2 {
    margin: 5px 0;
    font-size: 20px;
}

.mobile-network-form header p {
    margin: 0;
    color: #949dab;
    font-size: 12px;
}

.mobile-network-form__notice {
    display: flex;
    margin: 13px 10px 0;
    padding: 10px 11px;
    align-items: flex-start;
    color: #8b6d36;
    font-size: 11px;
    line-height: 1.55;
    background: #fff8e9;
    border: 1px solid #f0dfb9;
    border-radius: 9px;
    gap: 7px;
}

.mobile-network-form__notice .van-icon {
    flex: none;
    margin-top: 2px;
}

.mobile-network-form__submit {
    padding: 20px 10px 4px;
}

.mobile-network-form__submit /deep/ .van-button {
    background: linear-gradient(135deg, #8069dd, #5f48bd);
    border: 0;
}

.mobile-network-manager-form {
    max-height: 78vh;
    overflow-y: auto;
}

.mobile-network-manager-option strong, .mobile-network-manager-option span {
    display: block;
}

.mobile-network-manager-option strong {
    color: #30394a;
    font-size: 14px;
}

.mobile-network-manager-option span {
    margin-top: 3px;
    color: #9aa3b0;
    font-size: 11px;
}

@media (max-width: 360px) {
    .mobile-network-license-meta {
        grid-template-columns: 1fr;
    }
}
</style>
